/**
 * Google types, should match the Google API.
 */

import { LineString } from "geojson";

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/LatLng
interface LatLng {
  latitude: number;
  longitude: number;
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/Location
interface Location {
  latLng: LatLng;
  heading?: number;
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/TopLevel/computeRoutes#PolylineEncoding
enum PolylineEncoding {
  ENCODED_POLYLINE = "ENCODED_POLYLINE",
  GEO_JSON_LINESTRING = "GEO_JSON_LINESTRING",
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/TopLevel/computeRoutes
interface ComputeRoutesRequest {
  origin: WayPoint;
  destination: WayPoint;
  intermediates?: WayPoint[];
  travelMode?: RouteTravelMode;
  optimizeWaypointOrder?: boolean;
  units?: Units;
  languageCode?: string;
  regionCode?: string;
  polylineEncoding?: PolylineEncoding;
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/TopLevel/computeRoutes#RouteLabel
enum RouteLabel {
  ROUTE_LABEL_UNSPECIFIED = "ROUTE_LABEL_UNSPECIFIED",
  DEFAULT_ROUTE = "DEFAULT_ROUTE",
  DEFAULT_ROUTE_ALTERNATE = "DEFAULT_ROUTE_ALTERNATE",
  FUEL_EFFICIENT = "FUEL_EFFICIENT",
  SHORTER_DISTANCE = "SHORTER_DISTANCE",
}

interface Polyline {
  // Union field polyline_type can be only one of the following:
  encodedPolyline?: string;
  geoJsonLinestring?: LineString;
  // End of list of possible types for union field polyline_type.
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/TopLevel/computeRoutes#RouteLegStepTravelAdvisory
interface RouteLegStepTravelAdvisory {
  speedReadingIntervals: SpeedReadingInterval[];
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/LocalizedText
interface LocalizedText {
  text: string;
  languageCode: string;
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/TopLevel/computeRoutes#RouteLegStepLocalizedValues
interface RouteLegStepLocalizedValues {
  distance: LocalizedText;
  staticDuration: LocalizedText;
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/TopLevel/computeRoutes#TransitVehicle
interface TransitVehicle {
  iconUri: string;
  localIconUri: string;
  name: LocalizedText;
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/TopLevel/computeRoutes#TransitLine
interface TransitLine {
  name: string;
  uri: string;
  color: string;
  iconUri: string;
  nameShort: string;
  textColor: string;
  vehicle: TransitVehicle;
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/TopLevel/computeRoutes#LocalizedTime
interface LocalizedTime {
  time: LocalizedText;
  timeZone: string;
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/TopLevel/computeRoutes#TransitDetailsLocalizedValues
interface TransitDetailsLocalizedValues {
  arrivalTime: LocalizedTime;
  departureTime: LocalizedTime;
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/TopLevel/computeRoutes#TransitStop
interface TransitStop {
  name: string;
  location: Location;
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/TopLevel/computeRoutes#TransitStopDetails
interface TransitStopDetails {
  arrivalStop: TransitStop;
  arrivalTime: string;
  departureStop: TransitStop;
  departureTime: string;
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/TopLevel/computeRoutes#RouteLegStepTransitDetails
interface RouteLegStepTransitDetails {
  stopDetails: TransitStopDetails;
  localizedValues: TransitDetailsLocalizedValues;
  headsign: string;
  headway: string;
  transitLine: TransitLine;
  stopCount: number;
  tripShortText: string;
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/TopLevel/computeRoutes#RouteLegStep
interface RouteLegStep {
  distanceMeters: number;
  staticDuration: string;
  polyline: Polyline;
  startLocation: Location;
  endLocation: Location;
  navigationInstruction?: NavigationInstruction;
  travelAdvisory: RouteLegStepTravelAdvisory;
  localizedValues: RouteLegStepLocalizedValues;
  transitDetails: RouteLegStepTransitDetails;
  travelMode: RouteTravelMode;
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/TopLevel/computeRoutes#RouteLegTravelAdvisory
interface RouteLegTravelAdvisory {
  tollInfo: TollInfo;
  speedReadingIntervals: SpeedReadingInterval[];
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/TopLevel/computeRoutes#RouteLegLocalizedValues
interface RouteLegLocalizedValues {
  distance: LocalizedText;
  duration: LocalizedText;
  staticDuration: LocalizedText;
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/TopLevel/computeRoutes#Maneuver
enum Maneuver {
  TURN_SLIGHT_LEFT = "TURN_SLIGHT_LEFT",
  TURN_SHARP_LEFT = "TURN_SHARP_LEFT",
  UTURN_LEFT = "UTURN_LEFT",
  TURN_LEFT = "TURN_LEFT",
  TURN_SLIGHT_RIGHT = "TURN_SLIGHT_RIGHT",
  TURN_SHARP_RIGHT = "TURN_SHARP_RIGHT",
  UTURN_RIGHT = "UTURN_RIGHT",
  TURN_RIGHT = "TURN_RIGHT",
  STRAIGHT = "STRAIGHT",
  RAMP_LEFT = "RAMP_LEFT",
  RAMP_RIGHT = "RAMP_RIGHT",
  MERGE = "MERGE",
  FORK_LEFT = "FORK_LEFT",
  FORK_RIGHT = "FORK_RIGHT",
  FERRY = "FERRY",
  FERRY_TRAIN = "FERRY_TRAIN",
  ROUNDABOUT_LEFT = "ROUNDABOUT_LEFT",
  ROUNDABOUT_RIGHT = "ROUNDABOUT_RIGHT",
  DEPART = "DEPART",
  NAME_CHANGE = "NAME_CHANGE",
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/TopLevel/computeRoutes#NavigationInstruction
interface NavigationInstruction {
  maneuver: Maneuver;
  instructions: string;
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/TopLevel/computeRoutes#MultiModalSegment
interface MultiModalSegment {
  travelMode: RouteTravelMode;
  stepStartIndex: number;
  stepEndIndex: number;
  navigationInstruction: NavigationInstruction;
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/TopLevel/computeRoutes#StepsOverview
interface StepsOverview {
  multiModalSegments: MultiModalSegment[];
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/TopLevel/computeRoutes#RouteLeg
interface RouteLeg {
  distanceMeters: number;
  duration: string; // "1000s" is 1000 seconds
  staticDuration: string;
  polyline: Polyline;
  startLocation: Location;
  endLocation: Location;
  steps: RouteLegStep[];
  travelAdvisory: RouteLegTravelAdvisory;
  localizedValues: RouteLegLocalizedValues;
  stepsOverview: StepsOverview;
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/TopLevel/computeRoutes#Viewport
interface Viewport {
  low: LatLng;
  high: LatLng;
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/Money
interface Money {
  currencyCode: string;
  units: number;
  nanos: number;
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/TollInfo
interface TollInfo {
  estimatedPrice: Money[];
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/SpeedReadingInterval#Speed
enum Speed {
  SPEED_UNSPECIFIED = "SPEED_UNSPECIFIED",
  NORMAL = "NORMAL",
  SLOW = "SLOW",
  TRAFFIC_JAM = "TRAFFIC_JAM",
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/SpeedReadingInterval
interface SpeedReadingInterval {
  startPolylinePointIndex: number;
  endPolylinePointIndex: number;
  speed: Speed;
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/RouteTravelAdvisory
interface RouteTravelAdvisory {
  tollInfo: TollInfo;
  speedReadingIntervals: SpeedReadingInterval[];
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/TopLevel/computeRoutes#RouteLocalizedValues
interface RouteLocalizedValues {
  distance: LocalizedText;
  duration: LocalizedText;
  staticDuration: LocalizedText;
  transitFare: LocalizedText;
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/TopLevel/computeRoutes#RoadFeatureState
enum RoadFeatureState {
  ROAD_FEATURE_STATE_UNSPECIFIED = "ROAD_FEATURE_STATE_UNSPECIFIED",
  EXISTS = "EXISTS",
  DOES_NOT_EXIST = "DOES_NOT_EXIST",
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/TopLevel/computeRoutes#PolylinePointIndex
interface PolylinePointIndex {
  startIndex: number;
  endIndex: number;
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/TopLevel/computeRoutes#FlyoverInfo
interface FlyoverInfo {
  flyoverPresence: RoadFeatureState;
  polylinePointIndex: PolylinePointIndex;
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/TopLevel/computeRoutes#PolylineDetails
interface PolylineDetails {
  flyoverInfo: FlyoverInfo;
  narrowRoadInfo: any[];
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/TopLevel/computeRoutes#Route
interface Route {
  description: string;
  distanceMeters: number;
  duration: string; // Duration format string
  legs: RouteLeg[];
  localizedValues?: RouteLocalizedValues;
  optimizedIntermediateWaypointIndex?: number[];
  polyline: Polyline;
  polylineDetails?: PolylineDetails | {};
  routeLabels?: RouteLabel[];
  routeToken?: string;
  staticDuration: string; // "3250s"
  travelAdvisory?: RouteTravelAdvisory | {};
  viewport: Viewport;
  warnings?: string[];
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/Status
interface Status {
  code: number;
  message: string;
  details: { "@type": string; [key: string]: any }[];
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/TopLevel/computeRoutes#GeocodedWaypoint
interface GeocodedWaypoint {
  geocoderStatus: Status;
  type: string;
  partialMatch: boolean;
  placeId: string;
  intermediateWaypointRequestIndex: number;
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/TopLevel/computeRoutes#geocodingresults
interface GeocodingResults {
  origin: GeocodedWaypoint;
  destination: GeocodedWaypoint;
  intermediates: GeocodedWaypoint[];
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/TopLevel/computeRoutes#response-body
interface ComputeRoutesResponse {
  geocodingResults: GeocodingResults | {};
  routes: Route[];
  fallbackInfo?: any;
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/Units
enum Units {
  /**
   * Defaults to the unit of measure inferred from the request.
   */
  UNITS_UNSPECIFIED = "UNITS_UNSPECIFIED",
  METRIC = "METRIC",
  IMPERIAL = "IMPERIAL",
}

enum RouteTravelMode {
  /**
   * Defaults to driving.
   */
  TRAVEL_MODE_UNSPECIFIED = "TRAVEL_MODE_UNSPECIFIED",
  /**
   * Specifies a bicycling directions request.
   */
  BICYCLE = "BICYCLE",
  /**
   * Specifies a driving directions request.
   */
  DRIVE = "DRIVE",
  /**
   * Specifies a transit directions request.
   */
  TRANSIT = "TRANSIT",
  /**
   * Specifies a walking directions request.
   */
  WALK = "WALK",
}

// https://developers.google.com/maps/documentation/routes/reference/rest/v2/Waypoint
interface WayPoint {
  via?: boolean;
  vehicleStopover?: boolean;
  sideOfRoad?: boolean;
  // Union field location_type can be only one of the following:
  location?: Location;
  placeId?: string;
  address?: string;
  // End of list of possible types for union field location_type.
}

export type {
  ComputeRoutesResponse,
  WayPoint,
  ComputeRoutesRequest,
  GeocodedWaypoint,
  GeocodingResults,
  Status,
  LatLng,
  Location,
  Polyline,
  RouteLegStepTravelAdvisory,
  LocalizedText,
  TransitVehicle,
  TransitLine,
  LocalizedTime,
  TransitStop,
  TransitStopDetails,
  RouteLegStepTransitDetails,
  MultiModalSegment,
  StepsOverview,
  RouteLegTravelAdvisory,
  RouteLegLocalizedValues,
  PolylinePointIndex,
  FlyoverInfo,
  PolylineDetails,
  Route,
  Viewport,
  Money,
  TollInfo,
  SpeedReadingInterval,
  RouteLegStep,
  RouteLegStepLocalizedValues,
  NavigationInstruction,
  RouteLeg,
  TransitDetailsLocalizedValues,
  RouteTravelAdvisory,
  RouteLocalizedValues,
};

export {
  Maneuver,
  RouteLabel,
  Speed,
  Units,
  RouteTravelMode,
  RoadFeatureState,
  PolylineEncoding,
};
